import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Breadcrumbs,
  Button,
  CircularProgress,
  Typography,
} from "@mui/material";
import { Form, Modal } from "react-bootstrap";
import { Link } from "react-router-dom";
import { LoadingButton } from "@mui/lab";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import FactoryOutlinedIcon from "@mui/icons-material/FactoryOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import { imageInstance } from "../../Const/ApiHeader";
import {
  ADD_MANUFACTURER,
  ALL_MANUFACTURER,
  resolveImageUrl,
  UPDATE_MANUFACTURER,
} from "../../Const/ApiConst";

function resolveManufacturerLogoUrl(logo) {
  if (!logo) return null;

  const value = String(logo).trim();
  if (!value) return null;

  if (/^https?:\/\//i.test(value) || /manufacturers\//i.test(value)) {
    return resolveImageUrl(value);
  }

  const filename = value.split(/[/\\]/).pop();
  if (!filename) return null;
  return resolveImageUrl(`manufacturers/${filename}`);
}

function ManufacturerLogo({ src, name }) {
  const [failed, setFailed] = useState(false);

  if (!src || failed) {
    return (
      <div className="manufacturer-card-logo-fallback" aria-hidden>
        <FactoryOutlinedIcon />
        <span>{name?.charAt(0) || "?"}</span>
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={name}
      className="manufacturer-card-logo"
      onError={() => setFailed(true)}
    />
  );
}

function ManufacturerFormModal({
  show,
  title,
  saveLabel,
  loading,
  name,
  onNameChange,
  previewUrl,
  currentLogoUrl,
  onFileChange,
  onClose,
  onSave,
  requireFile,
}) {
  const canSave = name.trim() && (!requireFile || previewUrl || currentLogoUrl);

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="d-flex flex-column gap-3">
        <div className="manufacturer-modal-preview">
          {previewUrl ? (
            <img src={previewUrl} alt="Selected logo preview" />
          ) : currentLogoUrl ? (
            <img src={currentLogoUrl} alt="Current manufacturer logo" />
          ) : (
            <div className="manufacturer-modal-preview-empty">
              <ImageOutlinedIcon />
              <Typography variant="body2" color="text.secondary">
                Logo preview
              </Typography>
            </div>
          )}
        </div>

        <Form.Group>
          <Form.Label>Logo image</Form.Label>
          <Form.Control type="file" accept="image/*" onChange={onFileChange} />
        </Form.Group>

        <Form.Group>
          <Form.Label>Manufacturer name</Form.Label>
          <Form.Control
            type="text"
            placeholder="e.g. Toyota"
            value={name}
            onChange={(e) => onNameChange(e.target.value)}
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <LoadingButton
          variant="primary"
          loading={loading}
          disabled={!canSave}
          onClick={onSave}
        >
          {saveLabel}
        </LoadingButton>
      </Modal.Footer>
    </Modal>
  );
}

function Maufacturers() {
  const [manufacturerData, setManufacturerData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [saveLoading, setSaveLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [saveError, setSaveError] = useState("");

  const [openAddManufacturer, setOpenAddManufacturer] = useState(false);
  const [openUpdateManufacturer, setOpenUpdateManufacturer] = useState(false);

  const [updateId, setUpdateId] = useState("");
  const [manufactname, setManufactname] = useState("");
  const [manufactLogo, setManufactLogo] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");

  const resetForm = () => {
    setManufactname("");
    setManufactLogo("");
    setUpdateId("");
    setSelectedFile(null);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl("");
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    setSelectedFile(file || null);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(file ? URL.createObjectURL(file) : "");
  };

  const loadManufacturer = async () => {
    setLoading(true);
    setLoadError("");
    try {
      const response = await imageInstance.post(ALL_MANUFACTURER);
      setManufacturerData(response.data?.data || []);
    } catch (error) {
      console.log(error);
      setLoadError("Failed to load manufacturers.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadManufacturer();
  }, []);

  const query = search.trim().toLowerCase();
  const filteredManufacturers = useMemo(() => {
    if (!query) return manufacturerData;
    return manufacturerData.filter((item) =>
      (item.manufacturerName || "").toLowerCase().includes(query)
    );
  }, [manufacturerData, query]);

  const openAdd = () => {
    resetForm();
    setOpenAddManufacturer(true);
  };

  const closeAdd = () => {
    if (!saveLoading) {
      setOpenAddManufacturer(false);
      resetForm();
    }
  };

  const openEdit = (id, name, logo) => {
    resetForm();
    setUpdateId(id);
    setManufactname(name);
    setManufactLogo(logo);
    setOpenUpdateManufacturer(true);
  };

  const closeEdit = () => {
    if (!saveLoading) {
      setOpenUpdateManufacturer(false);
      resetForm();
    }
  };

  const saveManufact = async () => {
    if (!selectedFile || !manufactname.trim()) return;

    setSaveLoading(true);
    setSaveError("");
    try {
      const formData = new FormData();
      formData.append("manufactname", manufactname.trim());
      formData.append("logo", selectedFile);
      await imageInstance.post(ADD_MANUFACTURER, formData);
      await loadManufacturer();
      closeAdd();
    } catch (error) {
      console.log(error);
      setSaveError("Failed to add manufacturer.");
    } finally {
      setSaveLoading(false);
    }
  };

  const updateManufacturer = async () => {
    if (!updateId || !manufactname.trim()) return;

    setSaveLoading(true);
    setSaveError("");
    try {
      const formData = new FormData();
      formData.append("id", updateId);
      formData.append("manufactname", manufactname.trim());
      if (selectedFile) {
        formData.append("logo", selectedFile);
      }
      await imageInstance.post(UPDATE_MANUFACTURER, formData);
      await loadManufacturer();
      closeEdit();
    } catch (error) {
      console.log(error);
      setSaveError("Failed to update manufacturer.");
    } finally {
      setSaveLoading(false);
    }
  };

  return (
    <div className="vehicles-page">
      <Breadcrumbs aria-label="breadcrumb" className="mb-3">
        <Link to="/">Home</Link>
        <Typography>Manufacturers</Typography>
      </Breadcrumbs>

      {loadError ? (
        <Alert severity="error" className="mb-3">
          {loadError}
        </Alert>
      ) : null}
      {saveError ? (
        <Alert severity="error" className="mb-3">
          {saveError}
        </Alert>
      ) : null}

      <section className="vehicles-hero">
        <div>
          <Typography className="vehicles-hero-eyebrow">
            <FactoryOutlinedIcon fontSize="inherit" /> Brand catalogue
          </Typography>
          <Typography variant="h4" className="vehicles-hero-title">
            Manufacturers
          </Typography>
          <Typography className="vehicles-hero-subtitle">
            Manage brand logos and names shown in the mobile app catalogue.
          </Typography>
        </div>
        <Button variant="primary" onClick={openAdd}>
          <AddIcon className="me-1" /> Add Manufacturer
        </Button>
      </section>

      <section className="vehicles-stats-grid manufacturers-stats-grid">
        <div className="vehicles-stat-card">
          <Typography className="vehicles-stat-value">
            {manufacturerData.length}
          </Typography>
          <Typography className="vehicles-stat-label">Total brands</Typography>
        </div>
        <div className="vehicles-stat-card tone-blue">
          <Typography className="vehicles-stat-value">
            {filteredManufacturers.length}
          </Typography>
          <Typography className="vehicles-stat-label">Showing now</Typography>
        </div>
      </section>

      <section className="section vehicles-toolbar">
        <Form.Control
          className="admin-search"
          type="search"
          placeholder="Search manufacturers..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </section>

      <section className="manufacturers-results">
        {loading ? (
          <div className="manufacturers-loading">
            <CircularProgress size={32} />
            <Typography color="text.secondary">Loading manufacturers...</Typography>
          </div>
        ) : filteredManufacturers.length === 0 ? (
          <div className="section vehicles-empty">
            <FactoryOutlinedIcon className="vehicles-empty-icon" />
            <Typography variant="h6">
              {manufacturerData.length === 0
                ? "No manufacturers yet"
                : "No manufacturers match your search"}
            </Typography>
            <Typography color="text.secondary" className="mb-3">
              {manufacturerData.length === 0
                ? "Add your first brand to populate the app catalogue."
                : "Try a different search term."}
            </Typography>
            {manufacturerData.length === 0 ? (
              <Button variant="primary" onClick={openAdd}>
                <AddIcon className="me-1" /> Add Manufacturer
              </Button>
            ) : null}
          </div>
        ) : (
          <div className="manufacturers-grid">
            {filteredManufacturers.map((item) => {
              const logoUrl = resolveManufacturerLogoUrl(item.manufacturerLogo);

              return (
                <article key={item._id} className="manufacturer-card">
                  <div className="manufacturer-card-media">
                    <ManufacturerLogo src={logoUrl} name={item.manufacturerName} />
                  </div>
                  <div className="manufacturer-card-body">
                    <Typography className="manufacturer-card-name">
                      {item.manufacturerName}
                    </Typography>
                    <Typography className="manufacturer-card-meta">
                      Brand listing
                    </Typography>
                  </div>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<EditOutlinedIcon />}
                    onClick={() =>
                      openEdit(item._id, item.manufacturerName, item.manufacturerLogo)
                    }
                  >
                    Edit
                  </Button>
                </article>
              );
            })}
          </div>
        )}
      </section>

      <ManufacturerFormModal
        show={openAddManufacturer}
        title="Add manufacturer"
        saveLabel="Save manufacturer"
        loading={saveLoading}
        name={manufactname}
        onNameChange={setManufactname}
        previewUrl={previewUrl}
        onFileChange={handleFileChange}
        onClose={closeAdd}
        onSave={saveManufact}
        requireFile
      />

      <ManufacturerFormModal
        show={openUpdateManufacturer}
        title="Edit manufacturer"
        saveLabel="Update manufacturer"
        loading={saveLoading}
        name={manufactname}
        onNameChange={setManufactname}
        previewUrl={previewUrl}
        currentLogoUrl={resolveManufacturerLogoUrl(manufactLogo) || ""}
        onFileChange={handleFileChange}
        onClose={closeEdit}
        onSave={updateManufacturer}
      />
    </div>
  );
}

export default Maufacturers;
